import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState('user');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        console.log('[AuthProvider] getSession response', {
          hasSession: !!data?.session,
          userId: data?.session?.user?.id || null,
        });
        if (!isMounted) return;
        setSession(data?.session || null);
        setUser(data?.session?.user || null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    init();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      console.log('[AuthProvider] onAuthStateChange', {
        event,
        hasSession: !!nextSession,
        userId: nextSession?.user?.id || null,
      });
      setSession(nextSession);
      setUser(nextSession?.user || null);
    });

    return () => {
      isMounted = false;
      authListener?.subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) {
        setProfile(null);
        setRole('user');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('ch_profiles')
          .select('id, username, role')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          setProfile(null);
          setRole('user');
          return;
        }

        setProfile(data || null);
        setRole(data?.role || 'user');
      } catch {
        setProfile(null);
        setRole('user');
      }
    };

    loadProfile();
  }, [user?.id]);

  const value = useMemo(
    () => ({
      session,
      user,
      profile,
      role,
      loading,
      signOut: async () => {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setProfile(null);
        setRole('user');
      },
    }),
    [session, user, profile, role, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
